import { useMemo, useEffect, useState, useCallback, lazy, Suspense } from 'react';
import { useTranslation } from 'react-i18next';
import { useProjectStore } from '@/store/projectStore.ts';
import { useAiStore } from '@/store/aiStore.ts';
import { useAccountStore } from '@/store/accountStore.ts';
import { capabilities } from '@/capabilities.ts';
import { FileLoader } from '@/components/FileLoader.tsx';
import { TimelineView } from '@/components/TimelineView.tsx';
import { ReviewLoader } from '@/components/ReviewLoader.tsx';
import { EmbeddedLoader } from '@/components/EmbeddedLoader.tsx';
import { ErrorBoundary } from '@/components/ErrorBoundary.tsx';
import { AnimaticRegenDialog } from '@/components/AnimaticRegenDialog.tsx';
import { AnimaticEnhanceConfirmDialog } from '@/components/AnimaticEnhanceConfirmDialog.tsx';

const LazyCreditsPage = lazy(() =>
  import('@/components/CreditsPage.tsx').then(m => ({ default: m.CreditsPage }))
);

const LazyScreenplayPage = lazy(() =>
  import('@/components/ScreenplayPage.tsx').then(m => ({ default: m.ScreenplayPage }))
);

const LazyScreenplayManagerPage = lazy(() =>
  import('@/components/ScreenplayManager/ScreenplayManagerPage.tsx').then(m => ({ default: m.ScreenplayManagerPage }))
);

const LazySignInPage = lazy(() =>
  import('@/components/SignIn/SignInPage.tsx').then(m => ({ default: m.SignInPage }))
);

const LazyPurchasingPage = lazy(() =>
  import('@/components/Purchasing/PurchasingPage.tsx').then(m => ({ default: m.PurchasingPage }))
);

const LazyCreditPacksPage = lazy(() =>
  import('@/components/CreditPacksPage.tsx').then(m => ({ default: m.CreditPacksPage }))
);

const LazyDebugPage = lazy(() =>
  import('@/components/DebugPage.tsx').then(m => ({ default: m.DebugPage }))
);

const LazyPrevisAssistPage = lazy(() =>
  import('@/components/PrevisAssist/PrevisAssistPage.tsx').then(m => ({ default: m.PrevisAssistPage }))
);

const LazyWelcomePage = lazy(() =>
  import('@/components/Welcome/WelcomePage.tsx').then(m => ({ default: m.WelcomePage }))
);

const LazyLandingPage = lazy(() =>
  import('@/components/Landing/LandingPage.tsx').then(m => ({ default: m.LandingPage }))
);

const LazyAvatarCustomizerPage = lazy(() =>
  import('@/components/AvatarCustomizer/AvatarCustomizerPage.tsx').then((m) => ({ default: m.AvatarCustomizerPage })),
);

const LazyAvatarCreatePage = lazy(() =>
  import('@/components/AvatarCreate/AvatarCreatePage.tsx').then((m) => ({ default: m.AvatarCreatePage })),
);

const LazyProjectsPage = lazy(() =>
  import('@/components/Projects/ProjectsPage.tsx').then((m) => ({ default: m.ProjectsPage })),
);

const LazySceneBoardPage = lazy(() =>
  import('@/components/SceneBoard/SceneBoardPage').then((m) => ({ default: m.SceneBoardPage })),
);

const LazyCanvasOverlayPage = lazy(() =>
  import('@/components/CanvasOverlay/CanvasOverlayPage').then((m) => ({ default: m.CanvasOverlayPage })),
);

const LazyCanvasCoveragePage = lazy(() =>
  import('@/components/CanvasCoverage/CanvasCoveragePage').then((m) => ({ default: m.CanvasCoveragePage })),
);

export default function App() {
  const { t } = useTranslation('common');
  const project = useProjectStore(s => s.project);
  const accountUser = useAccountStore(s => s.accountUser);
  const authChecked = useAccountStore(s => s.authChecked);

  const [oauthError, setOauthError] = useState<string | null>(null);
  const [sampleUrl, setSampleUrl] = useState<string | null>(null);
  const [showLanding, setShowLanding] = useState(true);

  // Set body class for embedded mode CSS targeting (e.g. iPad safe-area compensation)
  useEffect(() => {
    if (capabilities.bridge) {
      document.body.classList.add('embedded');
    }
  }, []);

  // Detect ?session=<id> in URL (checked once on mount)
  const sessionParam = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('session');
  }, []);

  const creditsToken = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    return params.has('credits') ? params.get('token') : null;
  }, []);

  const pageParam = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('page');
  }, []);

  // On mount: listen for auth state changes (handles initial session + OAuth redirects)
  useEffect(() => {
    if (capabilities.bridge) {
      useAccountStore.getState().setAuthChecked(true);
      return;
    }

    // Credits page handles its own auth via the JWT token param
    if (creditsToken !== null) return;

    // Screenplay overlay & Screenplay Manager pages use the bridge — no auth needed
    if (pageParam === 'screenplay' || pageParam === 'screenplaymanager' || pageParam === 'signin' || pageParam === 'purchasing' || pageParam === 'creditpacks' || pageParam === 'welcome' || pageParam === 'debug' || pageParam === 'assist' || pageParam === 'avatar' || pageParam === 'avatarcreate' || pageParam === 'projects' || pageParam === 'scene-board' || pageParam === 'canvas-overlay' || pageParam === 'canvas-coverage') return;

    // Warm up model catalog (fire-and-forget)
    useAiStore.getState().loadModelCatalog();

    let cancelled = false;

    (async () => {
      const { getSupabase } = await import('@/api/supabaseClient.ts');
      const { extractDisplayName } = await import('@/api/accountApi.ts');
      const sb = await getSupabase();
      if (!sb || cancelled) {
        useAccountStore.getState().setAuthChecked(true);
        return;
      }

      function handleUser(user: { id: string; email?: string | null; is_anonymous?: boolean; user_metadata?: Record<string, unknown> } | null) {
        if (cancelled) return;
        if (user && !user.is_anonymous && !user.user_metadata?.is_anonymous) {
          const store = useAccountStore.getState();
          store.setAccountUser({
            id: user.id,
            email: user.email ?? '',
            isAnonymous: false,
            displayName: extractDisplayName(user),
          });
          store.refreshBalance();
        } else {
          useAccountStore.getState().setAccountUser(null);
        }
        useAccountStore.getState().setAuthChecked(true);
      }

      // Detect OAuth redirect (PKCE code in URL)
      const urlParams = new URLSearchParams(window.location.search);
      const oauthCode = urlParams.get('code');

      // Listen for auth changes (ongoing session refresh, sign-out, etc.)
      const { data: { subscription } } = sb.auth.onAuthStateChange((_event, session) => {
        handleUser(session?.user ?? null);
      });

      if (oauthCode) {
        // Explicit PKCE code exchange — don't rely on auto-detection.
        // In dev mode, React StrictMode double-invokes effects, so the second
        // attempt may fail (PKCE codes are single-use). Fall back to checking
        // for an existing session when the exchange fails.
        try {
          const { data, error } = await sb.auth.exchangeCodeForSession(oauthCode);
          if (error) {
            console.error('PKCE code exchange failed:', error.message);
            // Check if a session already exists (StrictMode double-mount)
            const { data: { session: existing } } = await sb.auth.getSession();
            if (existing?.user) {
              handleUser(existing.user);
            } else {
              setOauthError?.(error.message);
              handleUser(null);
            }
          } else {
            handleUser(data?.session?.user ?? null);
          }
        } catch (err) {
          console.error('PKCE exchange error:', err);
          // Fall back to existing session (StrictMode may have already exchanged)
          try {
            const { data: { session: existing } } = await sb.auth.getSession();
            handleUser(existing?.user ?? null);
          } catch {
            handleUser(null);
          }
        }
        // Clean up OAuth code from URL
        const url = new URL(window.location.href);
        url.searchParams.delete('code');
        window.history.replaceState({}, '', url.pathname + (url.searchParams.toString() ? '?' + url.searchParams.toString() : ''));
      } else {
        // Normal load — check existing session
        const { data: { session } } = await sb.auth.getSession();
        handleUser(session?.user ?? null);
      }

      // Handle ?purchase=success redirect from Stripe
      const params = new URLSearchParams(window.location.search);
      if (params.get('purchase') === 'success') {
        params.delete('purchase');
        const newUrl = params.toString()
          ? `${window.location.pathname}?${params.toString()}`
          : window.location.pathname;
        window.history.replaceState({}, '', newUrl);
        useAccountStore.getState().refreshBalance();
      }

      // Cleanup on unmount
      return () => {
        subscription.unsubscribe();
      };
    })().then(cleanup => {
      if (cancelled && cleanup) cleanup();
    });

    return () => { cancelled = true; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleGoogle = useCallback(async () => {
    setOauthError(null);
    try {
      const { signInWithGoogle } = await import('@/api/accountApi.ts');
      await signInWithGoogle();
    } catch (err) {
      setOauthError(err instanceof Error ? err.message : t('googleSignInFailed'));
    }
  }, [t]);

  const handleApple = useCallback(async () => {
    setOauthError(null);
    try {
      const { signInWithApple } = await import('@/api/accountApi.ts');
      await signInWithApple();
    } catch (err) {
      setOauthError(err instanceof Error ? err.message : t('appleSignInFailed'));
    }
  }, [t]);

  // Credits page: standalone purchase page opened from embedded app
  if (creditsToken !== null) {
    return (
      <Suspense fallback={null}>
        <LazyCreditsPage token={creditsToken} />
      </Suspense>
    );
  }

  // Screenplay overlay page: read-only screenplay display for canvas editor WebView
  if (pageParam === 'screenplay') {
    return (
      <Suspense fallback={null}>
        <LazyScreenplayPage />
      </Suspense>
    );
  }

  // Screenplay Manager page: browse/edit/diff screenplay data
  if (pageParam === 'screenplaymanager') {
    return (
      <Suspense fallback={null}>
        <LazyScreenplayManagerPage />
      </Suspense>
    );
  }

  // Sign-in page: embedded WebView UI for Unity sign-in flow
  if (pageParam === 'signin') {
    return (
      <Suspense fallback={null}>
        <LazySignInPage />
      </Suspense>
    );
  }

  // Purchasing page: embedded WebView UI for Unity IAP flow
  if (pageParam === 'purchasing') {
    return (
      <Suspense fallback={null}>
        <LazyPurchasingPage />
      </Suspense>
    );
  }

  // Credit packs page: embedded WebView UI for credit pack IAP
  if (pageParam === 'creditpacks') {
    return (
      <Suspense fallback={null}>
        <LazyCreditPacksPage />
      </Suspense>
    );
  }

  // Welcome page: post-purchase / post-signin onboarding dialog (paths 1-3 only)
  if (pageParam === 'welcome') {
    return (
      <Suspense fallback={null}>
        <LazyWelcomePage />
      </Suspense>
    );
  }

  // Debug harness: load project then switch between all views
  if (pageParam === 'debug') {
    return (
      <Suspense fallback={null}>
        <LazyDebugPage />
        {/* The harness reaches the AI workspace too; without a mount here a stranded confirm
            would park every later Enhance (the store answers false while one is pending). */}
        <AnimaticEnhanceConfirmDialog />
      </Suspense>
    );
  }

  // Previs Assist: wiki-grounded help dialog opened from every `?` button (M1)
  if (pageParam === 'assist') {
    return (
      <Suspense fallback={null}>
        <LazyPrevisAssistPage />
      </Suspense>
    );
  }

  // Avatar customizer: embedded-only controls panel for personalizing a 3D avatar
  if (pageParam === 'avatar') {
    return (
      <Suspense fallback={null}>
        <LazyAvatarCustomizerPage />
      </Suspense>
    );
  }

  // Avatar creation: standalone Tripo/Avaturn avatar-creation flow (design mock)
  if (pageParam === 'avatarcreate') {
    return (
      <Suspense fallback={null}>
        <LazyAvatarCreatePage />
      </Suspense>
    );
  }

  // Projects page (Shell route — project library)
  if (pageParam === 'projects') {
    return (
      <Suspense fallback={null}>
        <LazyProjectsPage />
      </Suspense>
    );
  }

  // Scene-board Workspace (M2 Slice 1 — flag-gated on the host; route available on web side regardless)
  if (pageParam === 'scene-board') {
    return (
      <Suspense fallback={null}>
        <LazySceneBoardPage />
        {/* See the mount below in the bridge branch. THIS is the mount that matters most: the
            scene-board shell hosts BOTH Enhance doors (SceneBoardExpanded's BoardEnhanceDialog on
            the Board tab, TimelineView's AI workspace on the Timeline tab), and it is the #1
            embedded target. */}
        <AnimaticEnhanceConfirmDialog />
      </Suspense>
    );
  }

  // 20260928 - Canvas overlay: the web hamburger / Scene info / Shot info / Preferences shown over
  // the live 3D canvas (Unity's CanvasWebOverlayPopupPanel). MUST stay above the bridge fallthrough
  // below — an embedded load of this route would otherwise render the Timeline. No Enhance door is
  // reachable from it, so it needs no AnimaticEnhanceConfirmDialog mount.
  if (pageParam === 'canvas-overlay') {
    return (
      <Suspense fallback={null}>
        <LazyCanvasOverlayPage />
      </Suspense>
    );
  }

  // 20261005 - Canvas Coverage overview: the board's Coverage overview in the canvas's left side
  // panel (Unity's ScreenplayWebViewController, in place of ?page=screenplay while its flag is on).
  // Same rule as canvas-overlay: MUST stay above the bridge fallthrough below. No Enhance door is
  // reachable from it either.
  if (pageParam === 'canvas-coverage') {
    return (
      <Suspense fallback={null}>
        <LazyCanvasCoveragePage />
      </Suspense>
    );
  }

  // Embedded mode: Unity pushes project data via the bridge — no auth needed
  if (capabilities.bridge) {
    if (!project) return <EmbeddedLoader />;
    return (
      <ErrorBoundary>
        <TimelineView />
        <AnimaticRegenDialog />
        {/* T3 (owner batch 5): the "Enhance an animatic?" confirm is host-free — it renders from
            animaticEnhanceConfirmStore alone, so handleEnhance can await it from either Enhance
            door without BoardEnhanceDialog or WorkspaceMain knowing it exists. It needs a mount on
            every route that can reach an Enhance door, and App's branches return early, so there
            are three: here (legacy embedded Timeline), the scene-board shell above, and the
            standalone TimelineView at the bottom. */}
        <AnimaticEnhanceConfirmDialog />
      </ErrorBoundary>
    );
  }

  // Auth loading state
  if (!authChecked) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: 'var(--bg-base)' }}>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16 }}>
          <div style={{ width: 32, height: 32, border: '3px solid var(--border-color)', borderTopColor: 'var(--accent)', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
          <p style={{ color: 'var(--text-secondary)', fontSize: 14, margin: 0 }}>{t('loading')}</p>
        </div>
      </div>
    );
  }

  // Landing page: shown to all standalone visitors (signed-in or not) until dismissed
  if (!project && !sessionParam && showLanding && !sampleUrl) {
    return (
      <Suspense fallback={null}>
        <LazyLandingPage
          onOpenFile={() => setShowLanding(false)}
          onTrySample={(url) => { setShowLanding(false); setSampleUrl(url); }}
        />
      </Suspense>
    );
  }

  // Sample project: load directly (read-only, no auth required)
  if (!project && sampleUrl) {
    return <FileLoader sampleUrl={sampleUrl} />;
  }

  // Auth gate for review sessions: require sign-in to join
  if (!accountUser && sessionParam) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: 'var(--bg-base)' }}>
        <div style={{
          display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16,
          padding: 48, background: 'var(--bg-elevated)', borderRadius: 12,
          border: '1px solid var(--border-subtle)', maxWidth: 400, textAlign: 'center',
        }}>
          <h2 style={{ fontSize: 18, fontWeight: 600, color: 'var(--text-primary)', margin: 0 }}>
            {t('previsProTimeline')}
          </h2>
          <p style={{ fontSize: 14, color: 'var(--text-secondary)', margin: 0, lineHeight: 1.5 }}>
            {t('signInToJoinReview')}
          </p>
          {oauthError && (
            <p style={{ fontSize: 13, color: '#f44336', margin: 0 }}>{oauthError}</p>
          )}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, width: '100%' }}>
            <button onClick={handleGoogle} style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
              width: '100%', padding: '10px 16px', borderRadius: 6,
              border: '1px solid rgba(255, 255, 255, 0.15)',
              background: 'rgba(255, 255, 255, 0.06)',
              color: 'var(--text-primary)', fontSize: 14, fontWeight: 500, cursor: 'pointer',
            }}>
              <svg style={{ width: 18, height: 18 }} viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" />
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
              </svg>
              {t('continueWithGoogle')}
            </button>
            <button onClick={handleApple} style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
              width: '100%', padding: '10px 16px', borderRadius: 6,
              border: '1px solid rgba(255, 255, 255, 0.15)',
              background: 'rgba(255, 255, 255, 0.06)',
              color: 'var(--text-primary)', fontSize: 14, fontWeight: 500, cursor: 'pointer',
            }}>
              <svg style={{ width: 18, height: 18 }} viewBox="0 0 24 24" fill="currentColor">
                <path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
              </svg>
              {t('continueWithApple')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // If a session param is present and no project is loaded yet, show ReviewLoader
  if (sessionParam && !project) {
    return <ReviewLoader sessionId={sessionParam} />;
  }

  if (!project) {
    return <FileLoader />;
  }

  return (
    <ErrorBoundary>
      <TimelineView />
      {/* Third mount — see the bridge branch above. Standalone and online-review both reach the AI
          workspace's Enhance arm through this TimelineView. */}
      <AnimaticEnhanceConfirmDialog />
    </ErrorBoundary>
  );
}
